import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Pill } from '~/components/pill';
import { serverMessage } from '~/lib/api/server-error';
import { useAvailableFilters, useFilterTemplateValueMutations } from '~/hooks/use-filter-template-values';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	templateId: number;
}

/** Pick from the filters not yet attached to a template and add them in bulk (legacy "add more filters" modal). */
export function AddFiltersDialog({ open, onOpenChange, templateId }: Props) {
	const { t } = useTranslation();
	const { data: available, isLoading } = useAvailableFilters(templateId, open);
	const { addBulk } = useFilterTemplateValueMutations(templateId);
	const [selected, setSelected] = useState<Set<number>>(new Set());

	useEffect(() => {
		if (open) setSelected(new Set());
	}, [open]);

	const toggle = (idx: number) =>
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(idx)) next.delete(idx);
			else next.add(idx);
			return next;
		});

	// Available (not-yet-attached) filters have no stable id, so select/key by array index.
	const onConfirm = async () => {
		const chosen = (available ?? []).filter((_, i) => selected.has(i));
		if (chosen.length === 0) {
			onOpenChange(false);
			return;
		}
		try {
			await addBulk.mutateAsync(chosen.map(f => ({ ...f, FilterTemplateId: templateId })));
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t('catalog.filterTemplates.addFiltersTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.filterTemplates.addFiltersSubtitle')}</DialogDescription>
				</DialogHeader>

				<div className="max-h-80 overflow-y-auto rounded-md border border-border">
					{isLoading ? (
						<div className="flex items-center justify-center gap-2 py-10 text-sm text-foreground-muted">
							<Loader2 size={16} className="animate-spin" />
							{t('common.search')}…
						</div>
					) : (available ?? []).length === 0 ? (
						<div className="py-10 text-center text-sm text-foreground-subtle">{t('catalog.filterTemplates.noAvailableFilters')}</div>
					) : (
						<ul className="divide-y divide-border-subtle">
							{(available ?? []).map((f, i) => (
								<li key={i} className="flex items-center gap-3 px-3 py-2.5 hover:bg-elevated">
									<Switch checked={selected.has(i)} onCheckedChange={() => toggle(i)} aria-label={f.FilterName ?? ''} />
									<span className="min-w-0 flex-1 truncate text-sm text-foreground">{f.FilterName || '—'}</span>
									{f.FilterTypeName && <Pill variant="neutral">{f.FilterTypeName}</Pill>}
								</li>
							))}
						</ul>
					)}
				</div>

				<DialogFooter>
					<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="button" onClick={onConfirm} disabled={addBulk.isPending || selected.size === 0}>
						{addBulk.isPending ? t('common.saving') : t('catalog.filterTemplates.addSelected', { count: selected.size })}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
